import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TableMobile, PrimaryButton } from "extranet-component-library";

import arrow from '../assets/img/ARROW.svg';
import { WalletOperation } from "../models";

interface WalletProps {
    movements: Array<any>;
    totalItems: number;
    getMoreData: (page?: number, resultsPerPage?: number) => Promise<void>;
}

const WalletMobile = ({ movements, totalItems, getMoreData }: WalletProps) => {
    const { t } = useTranslation();
    const [showMore, setShowMore] = useState(false);
    const [showDetail, setShowDetail] = useState(-1);
    const [operationCards, setOperations] = useState<Array<any>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [numberPag, setNumberPag] = useState(1);
    const cardsPerPage = 5;
    const ref = useRef(cardsPerPage);
    let arrayForHoldingCards: any = [];

    const loopWithSlice = (start: number, end: number) => {
        const slicedCards = movements.slice(start, end);
        arrayForHoldingCards = [...arrayForHoldingCards, ...slicedCards];
        setOperations(arrayForHoldingCards);
        setShowMore(end < totalItems);
    };

    const handleShowMore = () => {
        const end = ref.current + cardsPerPage;
        
        if (ref.current >= movements.length) {
            setIsLoading(true);
            const page = numberPag + 1;

            getMoreData(page).then(() => {
                setNumberPag(page);
                loopWithSlice(0, end);
                ref.current += cardsPerPage;

                setIsLoading(false);
            });
        } else {
            loopWithSlice(0, end);
            ref.current += cardsPerPage;
        }
    };

    const handleShowDetail = (index: number) => {
        setShowDetail(showDetail === index ? -1 : index)
    }

    const showArrow = (operation: WalletOperation) => {
        return (
            operation.info &&
            operation.info.data &&
            operation.info.data.cancellations &&
            operation.info.data.cancellations.length
        );
    };

    useEffect(() => {
        loopWithSlice(0, cardsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [movements.length]);

    return (
        <div className="wallet card" data-testid="walletmobile">
            {operationCards.length > 0 &&
                operationCards.map((operation, i) => {
                    return (
                        <TableMobile
                            key={i}
                            style={{
                                background: i % 2 !== 0 ? "#F3F3F3" : "white",
                                width: "100%",
                                marginBottom: "1rem",
                            }}
                            content={
                                <div className="flex-container">
                                    <div>
                                        <div className="item">
                                            <div className="header">
                                                {t("headers.operationDate")}
                                            </div>
                                            {operation.operationDate}
                                        </div>
                                        <div className="item">
                                            <div className="header">
                                                {t("headers.movements")}
                                            </div>
                                            {operation.movements}
                                        </div>
                                        <div className="item">
                                            <div className="header">
                                                {t("headers.amount")}
                                            </div>
                                            {operation.amount}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="item">
                                            <div className="header">
                                                {t("headers.totalAmount")}
                                            </div>
                                            {operation.totalAmount}
                                        </div>
                                        <div className="item">
                                            <div className="header">
                                                {t("headers.points")}
                                            </div>
                                            {operation.points}
                                        </div>
                                        <div className="item">
                                            <div className="header">
                                                {t("headers.totalPoints")}
                                            </div>
                                            {operation.totalPoints}
                                        </div>
                                        {operation.tripHops ?
                                            <div className="item">
                                                <div className="header">
                                                    {t("headers.tripHops")}
                                                </div>
                                                {operation.tripHops}
                                            </div>
                                            : null}
                                    </div>
                                </div>
                            }
                            arrowDown={arrow}
                            showArrow={showArrow(operation)}
                            detailContent={
                                <div className="flex-container">
                                    {showArrow(operation) &&
                                        showDetail === i &&
                                        operation.info.data.cancellations.map(
                                            (item: any, index: number) => {
                                                return (
                                                    <div key={index}>
                                                        <div className="item">
                                                            <div className="header">
                                                                {t("headers.date")}
                                                            </div>
                                                            {item.validationTime}
                                                        </div>
                                                        <div className="item">
                                                            <div className="header">
                                                                {t("headers.type")}
                                                            </div>
                                                            {item.type && item.type.startsWith('MISSING') ? 'error' : item.type}
                                                        </div>
                                                        <div className="item">
                                                            <div className="header">
                                                                {t("headers.line")}
                                                            </div>
                                                            {item.lineCode && item.address ? `${item.lineCode} ${item.address}` : '-'}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                </div>
                            }
                            expanded={showDetail === i}
                            onExpandDetail={() => handleShowDetail(i)}
                        />
                    );
                })}
            {showMore && (
                <div className="load-more">
                    <PrimaryButton
                        text={t("load_more")}
                        onClick={() => handleShowMore()}
                    />
                </div>
            )}
            {isLoading && (
                <div className="overlay">
                    <div className="loader" />
                </div>
            )}
        </div>
    );
};

export default WalletMobile;
